// Scanning a NEW RF sensor — the installer says what it is, then does what the
// app asks (open the door, close it, press the button) and each code is captured
// against the meaning it was asked for.
//
// ⚠️ THE MEANING IS OBSERVED, NOT CHOSEN. Which code is "Open" cannot be derived
// from the code (the event byte is the vendor's — see swapPair in rfTypes), so
// the old flow had the installer pick it from a list afterwards and it was saved
// back-to-front on a real site. Here the code heard while the app said "open the
// door" IS Open.
//
// ⚠️ A CODE IS ACCEPTED ONLY WHEN HEARD TWICE IN ITS STEP. A receiver passes on
// corrupted frames: a live log had 12026627 arrive inside a burst of 11764483,
// one bit apart, once. Taken at face value that is a new sensor. A real press
// repeats, and pressing again is cheap; a one-bit glitch does not repeat. Two
// hits may be two frames of one burst or two presses — either proves it.
//
// Pure: no MQTT, no React. core/hooks/useRfScan feeds it frames.

import { transmitterOf } from './rfCodes';
import { rfType } from './rfTypes';

// The four the installer reaches for first; the rest stay one tap away.
export const SCAN_PRIMARY_TYPES = ['panic', 'door', 'motion', 'shutter'];

export const HITS_TO_CONFIRM = 2;

// What to ask, per type. `optional` steps can be skipped (a shutter without a
// Stop code, a remote with fewer keys).
const STEP_TEXT = {
  panic: [{ meaning: 'Panic', prompt: 'Press the panic button.' }],
  motion: [{ meaning: 'Motion', prompt: 'Walk or wave in front of the sensor.' }],
  door: [
    { meaning: 'Open', prompt: 'Open the door — move the magnet AWAY from the sensor.' },
    { meaning: 'Close', prompt: 'Close the door — bring the magnet back.' },
  ],
  window: [{ meaning: 'Open', prompt: 'Open the window — move the magnet AWAY. It sends nothing on closing.' }],
  safe: [
    { meaning: 'Open', prompt: 'Open the safe door — move the magnet AWAY.' },
    { meaning: 'Close', prompt: 'Close the safe door — bring the magnet back.' },
  ],
  shutter: [
    { meaning: 'Open', prompt: 'Raise the shutter (Open).' },
    { meaning: 'Close', prompt: 'Lower the shutter (Close).' },
    { meaning: 'Stop', prompt: 'Stop the shutter, if it sends a Stop.', optional: true },
  ],
  fire: [{ meaning: 'Alarm', prompt: 'Press the sensor\'s TEST button.' }],
  gas: [{ meaning: 'Alarm', prompt: 'Press the sensor\'s TEST button.' }],
  water: [{ meaning: 'Alarm', prompt: 'Touch the probes with a wet finger, or press TEST.' }],
  remote: [
    { meaning: 'Key 1', prompt: 'Press key 1.' },
    { meaning: 'Key 2', prompt: 'Press key 2.', optional: true },
    { meaning: 'Key 3', prompt: 'Press key 3.', optional: true },
    { meaning: 'Key 4', prompt: 'Press key 4.', optional: true },
  ],
  other: [{ meaning: 'Event', prompt: 'Trigger the device.' }],
};

/** The steps for a type: [{ meaning, prompt, optional }]. */
export function scanSteps(typeKey) {
  return STEP_TEXT[rfType(typeKey).key] || STEP_TEXT.other;
}

/** A fresh scan. */
export function startScan(typeKey) {
  return {
    type: rfType(typeKey).key,
    steps: scanSteps(typeKey),
    step: 0,
    hits: {},          // code -> { count, binary, receiverUno } for THIS step
    captured: [],      // [{ meaning, code, binary, receiverUno }]
    notice: null,      // the last thing worth telling the installer
  };
}

export const scanDone = (s) => !!s && s.step >= s.steps.length;
export const currentStep = (s) => (s && !scanDone(s) ? s.steps[s.step] : null);

/**
 * One frame heard.
 *
 * @param known  Map code(string) -> { name, meaning } for codes that already
 *               belong to a NAMED sensor — those are reported, never captured.
 */
export function onFrame(state, frame, known = new Map()) {
  if (!state || scanDone(state) || !frame) return state;
  const code = Number(frame.code);
  if (!Number.isFinite(code) || code <= 0) return state;
  const key = String(code);

  // A code already captured in an earlier step: the installer opened the door
  // again while we wait for Close. Expected, and not a candidate.
  if (state.captured.some((c) => String(c.code) === key)) return state;

  const owner = known.get(key);
  if (owner) {
    return {
      ...state,
      notice: { kind: 'known', code, name: owner.name, meaning: owner.meaning },
    };
  }

  const prev = state.hits[key];
  const count = (prev ? prev.count : 0) + 1;
  const hit = {
    count,
    binary: frame.binary || (prev && prev.binary) || '',
    receiverUno: frame.receiverUno || (prev && prev.receiverUno) || '',
  };
  if (count < HITS_TO_CONFIRM) {
    return {
      ...state,
      hits: { ...state.hits, [key]: hit },
      notice: { kind: 'once', code },
    };
  }
  return advance(state, {
    meaning: state.steps[state.step].meaning,
    code,
    binary: hit.binary,
    receiverUno: hit.receiverUno,
  });
}

function advance(state, capturedRow) {
  const captured = capturedRow ? [...state.captured, capturedRow] : state.captured;
  return {
    ...state,
    step: state.step + 1,
    hits: {},
    captured,
    notice: capturedRow ? { kind: 'captured', code: capturedRow.code, meaning: capturedRow.meaning } : null,
  };
}

/** Skip an optional step. A required one cannot be skipped. */
export function skipStep(state) {
  const step = currentStep(state);
  if (!step || !step.optional) return state;
  return advance(state, null);
}

/** Do this step again — the code taken was the wrong one. */
export function redoLast(state) {
  if (!state || state.step === 0) return state;
  const prevStep = state.step - 1;
  const meaning = state.steps[prevStep].meaning;
  return {
    ...state,
    step: prevStep,
    hits: {},
    captured: state.captured.filter((c) => c.meaning !== meaning),
    notice: null,
  };
}

/**
 * Do the captured codes name ONE sensor?
 *
 * ⚠️ The server files each code under `transmitterOf(code)`, so codes from two
 * transmitter ids become two sensors whatever the app intended. That happens
 * when two different devices were triggered — or when this hardware does not
 * split its code the way rfCodes assumes (24-bit receivers, see docs/17).
 * Either way saving would be wrong, so the scan stops and says so.
 *
 * -> { ok, transmitterId, transmitters: [ids] }
 */
export function scanTransmitter(state) {
  const ids = [...new Set((state?.captured || []).map((c) => transmitterOf(c.code)))];
  return { ok: ids.length === 1, transmitterId: ids.length === 1 ? ids[0] : 0, transmitters: ids };
}

/**
 * codes that already belong to a NAMED sensor, from GetRfDevices rows.
 * An unnamed (backfilled / auto-created) sensor does not count — scanning is
 * exactly how it gets named.
 */
export function namedCodeIndex(devices) {
  const map = new Map();
  (devices || []).forEach((d) => {
    const name = String(d?.Name || '').trim();
    if (!name) return;
    (d.Codes || []).forEach((c) => {
      map.set(String(c.REMOTEVALUE), { name, meaning: String(c.DeviceAction || '').trim() });
    });
  });
  return map;
}
